import { API, HOST } from '@/utils/http'
import { toast } from "@/components/ui/toast/use-toast"

export const setUser = ({commit}, user) => commit('SET_USER', user)

export const setMember = ({commit}, member) => commit('SET_MEMBER', member)

export const setLanguage = ({commit}, lang) => commit('SET_LANGUAGE', lang)

export const setMainMenuDrawer = ({commit}, isMini) => commit('SET_MAIN_MENU_DRAWER', isMini)

export const changeTheme = ({commit}, mode) => commit('SET_THEME_MODE', mode)

export const setProjectsFilters = ({commit}, showFilters) => commit('SET_PROJECTS_FILTERS', showFilters)

export const updateAvatar = async ({commit}, payload) => {
  try {
    const response = await API({
      headers: {'content-type': 'multipart/form-data'},
      method: 'PATCH',
      url: `${HOST}/member/update-avatar`,
      data: {
        file: payload.file
      }
    })

    commit('SET_MEMBER', response.data)

    toast({ title: 'Avatar updated' })
  } catch (e) {
    throw new Error("Can't update image. " + e)
  }
}

export const update = async ({ commit }, payload) => {
  try {
    const response = await API({
      method: 'PATCH',
      url: `${HOST}/member/update`,
      data: {
        display_name: payload.displayName,
        first_name: payload.first_name,
        last_name: payload.last_name
      }
    })

    commit('SET_MEMBER', response.data)
    toast({ title: 'User profile updated' })
  } catch (e) {
    throw new Error("Can't update member. " + e)
  }
}

export const setupSettings = ({ commit, state }) => {
  const storageSettings = localStorage.getItem('settings')
  if (storageSettings) {
    const parsed = JSON.parse(storageSettings)
    // Migrate old themeDark boolean to themeMode string
    if ('themeDark' in parsed && !('themeMode' in parsed)) {
      parsed.themeMode = parsed.themeDark ? 'dark' : 'light'
      delete parsed.themeDark
      localStorage.setItem('settings', JSON.stringify(parsed))
    }
    commit('SET_SETTINGS', parsed)
  } else {
    if (Intl) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      commit('SET_TZ', tz)
    }
  }

}

export const saveSettings = ({ state }) => {
  localStorage.setItem('settings', JSON.stringify(state.settings))
}

export const updateLang = ({ commit }, payload) => {
  commit('SET_LANG', payload.language)
}

export const updateTz = ({ commit }, payload) => {
  commit('SET_TZ', payload.tz)
}

export const updateTimeFormat = ({ commit }, payload) => {
  commit('SET_TIME_FORMAT', payload.timeFormat)
}

export const handleAuthentication = async ({ dispatch, commit }, { firebaseToken, inviteCode = null, inviteDetails = null, inviteType = null }) => {
  try {
    const tokens = await dispatch('getAuthTokens', { firebaseToken, inviteCode, inviteDetails, inviteType })
    if (!tokens) {
      // User not registered in backend
      return { success: false, error: 'USER_NOT_REGISTERED', reason: 'user_not_registered' }
    }

    // Check if email is not verified
    if (tokens.emailNotVerified) {
      return { success: false, error: 'EMAIL_NOT_VERIFIED', reason: 'email_not_verified', message: tokens.message }
    }

    localStorage.setItem('accessToken', tokens.access_token)
    localStorage.setItem('refreshToken', tokens.refresh_token)

    const response = await API({
      method: 'GET',
      url: `${HOST}/member`
    })

    const member = response.data
    if (!member) {
      return { success: false, reason: 'member_not_found' }
    }

    await dispatch('setMember', member)
    commit('SET_IS_LOGGEDIN', true, { root: true })

    if (window.Sentry && member.email) {
      window.Sentry.setUser({ email: member.email })
    }

    await dispatch('loadInitialData')

    return { success: true, member }
  } catch (error) {
    return { success: false, reason: 'error', error }
  }
}

// Exchange the stored refresh token for a fresh platform access token.
// Used by the WebSocket client when the server rejects its token (close 4001):
// the socket must not replay a token the server has already refused, and the
// token it holds may be a Firebase ID token rather than a platform one, which
// fails signature verification server-side.
// Returns the new access token, or null if refresh is not possible — in which
// case the caller should stop retrying and let the normal auth flow re-run.
export const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) return null

  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/token/refresh/`,
      data: { refresh_token: refreshToken }
    })

    const { access_token, refresh_token } = response.data || {}
    if (!access_token) return null

    localStorage.setItem('accessToken', access_token)
    if (refresh_token) localStorage.setItem('refreshToken', refresh_token)
    return access_token
  } catch (e) {
    // A refused refresh token means the session is genuinely gone; the Firebase
    // auth listener in App.vue is what recovers from that, not the socket.
    return null
  }
}

export const getAuthTokens = async ({ commit }, { firebaseToken, inviteCode = null, inviteDetails = null, inviteType = null }) => {
  try {
    let response

    if (inviteType === 'map' && inviteCode && inviteDetails) {
      // Map invites use their own endpoint, kept separate from project/org invites.
      response = await API({
        method: 'POST',
        url: `${HOST}/map-invite/${inviteCode}/accept`,
        data: {
          firebase_token: firebaseToken,
          first_name: inviteDetails.first_name,
          last_name: inviteDetails.last_name,
          email: inviteDetails.email || null,
          phone: inviteDetails.phone || null
        }
      })
    } else if (inviteCode && inviteDetails) {
      response = await API({
        method: 'POST',
        url: `${HOST}/token/invite/`,
        data: {
          firebase_token: firebaseToken,
          invite_code: inviteCode,
          first_name: inviteDetails.first_name,
          last_name: inviteDetails.last_name,
          email: inviteDetails.email || null,
          phone: inviteDetails.phone || null
        }
      })
    } else {
      // Regular login
      response = await API({
        method: 'POST',
        url: `${HOST}/token/`,
        data: {
          firebase_token: firebaseToken,
          invite_code: inviteCode
        }
      })
    }

    return response.data
  } catch (error) {
    // Check for USER_NOT_REGISTERED error
    if (error.response?.status === 403 && error.response?.data?.detail?.error === 'USER_NOT_REGISTERED') {
      console.log('[getAuthTokens] User not registered')
      // Return null to indicate user not registered
      return null
    }
    // Check for EMAIL_NOT_VERIFIED error
    if (error.response?.status === 403 && error.response?.data?.detail?.error === 'EMAIL_NOT_VERIFIED') {
      console.log('[getAuthTokens] Email not verified')
      // Return a special object to indicate email not verified
      return { emailNotVerified: true, message: error.response.data.detail.message }
    }
    throw error
  }
}

export const loadInitialData = async ({ dispatch }) => {
  try {
    await dispatch('org/fetchOrgs', null, { root: true })
    await dispatch('project/fetchProjects', null, { root: true })
    await dispatch('project/fetchAllMembers', null, { root: true })
    await dispatch('project/fetchAllTags', null, { root: true })
    await dispatch('project/fetchAllInvites', null, { root: true })
    return true
  } catch (error) {
    console.error('Error loading initial data:', error)
    throw error
  }
}
